import React from 'react';

import { concludeLongRunningUpload } from '../../../../store/slices/longRunningOperationsSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { ComponentDesignInProgress, ProgressProblem } from '../ComponentDesignListItem';
import { LongRunningProgressDialog } from '../LongRunningProgressDialog';

export function UploadDialog() {
  const dispatch = useAppDispatch();
  const state = useAppSelector((s) => s.longRunningOperations.upload);

  if (state == null) return null;

  const finishedUploading = state.alreadyUploadedComponentNames.length === state.allComponentNames.length;
  const finishedProcessing = state.finalized;

  const getComponentCategory = (name: string) => {
    if (state.currentlyUploadingComponentNames.includes(name)) return 'working';
    if (state.alreadyUploadedComponentNames.includes(name)) return 'finished';

    return 'waiting';
  };

  const getOverallStatusMessage = () => {
    if (finishedProcessing) {
      return Object.keys(state.errors).length > 0
        ? 'Finished, but some components failed to upload.'
        : `Finished uploading all components successfully${Object.keys(state.warnings).length > 0 ? ' with warnings' : ''}.`;
    }

    if (finishedUploading) {
      return 'Finalizing uploaded components...';
    }

    const nextComponentNumber = state.alreadyUploadedComponentNames.length + 1;
    return `Now uploading component ${nextComponentNumber} / ${state.allComponentNames.length} ...`;
  };

  const getComponentStatusMessage = (name: string): string => {
    if (state.currentlyUploadingComponentNames.includes(name)) return 'Uploading now...';
    if (state.alreadyUploadedComponentNames.includes(name))
      return name in state.errors
        ? 'Failed to upload.'
        : `Successfully uploaded${Object.keys(state.warnings).length > 0 ? ' with warnings' : ''}.`;

    return 'Waiting...';
  };

  const componentDesigns = state.allComponentNames.map((name): ComponentDesignInProgress => ({
    name,
    category: getComponentCategory(name),
    statusMessage: getComponentStatusMessage(name),
    problems: (state.errors[name] ?? [])
      .map((message): ProgressProblem => ({ type: 'error', message }))
      .concat((state.warnings[name] ?? []).map((message) => ({ type: 'warning', message }))),
  }));

  return (
    <LongRunningProgressDialog
      collapsible
      title="Uploading Component Designs"
      finished={state.finalized}
      statusMessage={getOverallStatusMessage()}
      componentDesigns={componentDesigns}
      onClose={() => void dispatch(concludeLongRunningUpload())}
    />
  );
}
